/**
 * `useMyTeams` — the caller's teams in the active tenant (teams-mgmt-api
 * `GET /teams`, hydrated with names) over the shared `ApiClient`.
 *
 * Powers the Team page's team switcher + "New team" flow. Keyed by the active
 * tenant so a tenant/org switch refetches, and returns an empty list when the
 * wired `TeamsApi` doesn't offer `listTeams` (optional on the port).
 */
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useSession } from "@authe/ui-core/session";
import type { TeamRef } from "@authe/fe-commons/teams";
import { useTeamsApi } from "@/api/adapters";

export interface UseMyTeamsResult {
  teams: TeamRef[];
  isLoading: boolean;
  error: unknown;
  refetch: () => void;
  /** Invalidate the cache so the next read re-fetches (after creating a team). */
  invalidate: () => void;
}

/** Stable empty list — see `useOrgRoles`: a fresh `[]` per render feeds effect loops. */
const NO_TEAMS: TeamRef[] = [];

export function useMyTeams(options?: { enabled?: boolean }): UseMyTeamsResult {
  const api = useTeamsApi();
  const queryClient = useQueryClient();
  const { session } = useSession();
  const tenantId = session?.tenant.tenantId ?? "";
  const queryKey = ["my-teams", session?.tenant.customerId, session?.tenant.orgId, tenantId, session?.user.id];

  const query = useQuery({
    queryKey: ["my-teams", session?.tenant.customerId, session?.tenant.orgId, tenantId, session?.user.id],
    queryFn: (): Promise<TeamRef[]> => (api.listTeams ? api.listTeams() : Promise.resolve([])),
    enabled: (options?.enabled ?? true) && Boolean(tenantId) && Boolean(api.listTeams),
    refetchInterval: 60_000,
  });

  return {
    teams: query.data ?? NO_TEAMS,
    isLoading: query.isLoading,
    error: query.error,
    refetch: () => void query.refetch(),
    invalidate: () => void queryClient.invalidateQueries({ queryKey }),
  };
}
